import { useMemo } from 'react';

import { isExperimentEnabled } from '@confluence/feature-experiments';

import {
	isDevDocsOwnedFolder,
	type DevDocsContentPropertyNodes,
} from './DevDocsFolderOwnership.ts';

export type DevDocsFolderPropertySources = {
	readonly contentProperties?: DevDocsContentPropertyNodes;
	readonly devDocsOwnershipProperties?: DevDocsContentPropertyNodes;
	readonly devDocsProperties?: DevDocsContentPropertyNodes;
	readonly propertyCollections?: readonly DevDocsContentPropertyNodes[];
	readonly smartFolderProperties?: DevDocsContentPropertyNodes;
};

export type DevDocsFolderEligibility = {
	readonly isDevDocsEnabled: boolean;
	readonly isDevDocsEligible: boolean;
	readonly isDevDocsOwned: boolean;
};

const getPropertyCollections = ({
	contentProperties,
	devDocsOwnershipProperties,
	devDocsProperties,
	propertyCollections,
	smartFolderProperties,
}: DevDocsFolderPropertySources): readonly DevDocsContentPropertyNodes[] => [
	...(propertyCollections ?? []),
	contentProperties,
	devDocsOwnershipProperties,
	devDocsProperties,
	smartFolderProperties,
];

export const useDevDocsFolderEligibility = (
	sources: DevDocsFolderPropertySources,
): DevDocsFolderEligibility => {
	const {
		contentProperties,
		devDocsOwnershipProperties,
		devDocsProperties,
		propertyCollections,
		smartFolderProperties,
	} = sources;
	const isDevDocsEnabled = isExperimentEnabled('confluence_dev_docs');
	const isDevDocsOwned = useMemo(
		() =>
			isDevDocsOwnedFolder({
				propertyCollections: getPropertyCollections({
					contentProperties,
					devDocsOwnershipProperties,
					devDocsProperties,
					propertyCollections,
					smartFolderProperties,
				}),
			}),
		[
			contentProperties,
			devDocsOwnershipProperties,
			devDocsProperties,
			propertyCollections,
			smartFolderProperties,
		],
	);

	return useMemo(
		() => ({
			isDevDocsEnabled,
			isDevDocsEligible: isDevDocsEnabled && isDevDocsOwned,
			isDevDocsOwned,
		}),
		[isDevDocsEnabled, isDevDocsOwned],
	);
};
